import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { listDocumentsReport, exportDocumentsReportCsv, fetchDocumentTypes, fetchDocumentsSummary, fetchReportDocumentTypes, sendDocumentReportEmail } from '../../../../services/reports';
import { fetchCustomerOrganisations } from '../../../../services/organisations';
import apiClient from '../../../../services/apiClient';
import API_ENDPOINTS from '../../../../Api/endpoints';
import { getCurrentUser } from '../../../../services/auth';

const daysOptions = ['EXPIRED', 30, 60, 90, 150];

// Pretty labels for canonical types
const labelForType = (t) => {
  switch (t) {
    case 'PASSPORT_COPY':
      return 'Passport Copy';
    case 'EMIRATES_ID':
      return 'Emirates ID';
    case 'VISA_PAGE':
      return 'Visa Page';
    case 'PHOTO':
      return 'Photo';
    case 'salaryCertificate':
      return 'Salary Certificate';
    case 'ALL':
      return 'ALL';
    default:
      // Fallback humanize
      try {
        const s = String(t || '')
          .replace(/_/g, ' ')
          .replace(/([a-z])([A-Z])/g, '$1 $2')
          .trim();
        return s.charAt(0).toUpperCase() + s.slice(1);
      } catch {
        return t;
      }
  }
};

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

function SummaryTile({ title, value, color = '#1976d2', onClick, selected = false, valueSx }) {
  return (
    <Box
      sx={{
        flex: 1,
        p: 2,
        borderRadius: 2,
        bgcolor: '#fff',
        border: '1px solid',
        borderColor: selected ? 'primary.light' : '#e5e7eb',
        boxShadow: selected ? '0 2px 8px rgba(0,0,0,0.06)' : '0 1px 3px rgba(0,0,0,0.04)',
        position: 'relative',
        overflow: 'hidden',
        transition: 'border-color 120ms ease, box-shadow 120ms ease, transform 120ms ease',
        '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.08)', transform: 'translateY(-1px)' },
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          bgcolor: color,
          opacity: 0.9,
        },
      }}
    >
      <Typography variant="body2" sx={{ color: '#334155', fontWeight: 600, mb: 0.5 }}>
        {title}
      </Typography>
      <Typography
        variant="h5"
        sx={{
          color,
          cursor: onClick ? 'pointer' : 'default',
          fontWeight: 800,
          lineHeight: 1.2,
          ...(valueSx || {}),
        }}
        onClick={onClick}
        title={onClick ? 'Click to filter list' : ''}
      >
        {value}
      </Typography>
    </Box>
  );
}

export default function ExpiringDocumentsPage() {
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);
  const [totalElements, setTotalElements] = useState(0);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [organizationId, setOrganizationId] = useState('');
  const [days, setDays] = useState(60);
  // Force show only current documents
  const [isCurrent] = useState(true);
  const [showAllDocs, setShowAllDocs] = useState(false);
  const [types, setTypes] = useState(['ALL']);
  const [typeOptions, setTypeOptions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [orgOptions, setOrgOptions] = useState([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const [tileKey, setTileKey] = useState(''); // '', 'TOTAL', 'R0_30', 'R31_60', 'R61_90', 'EXPIRED'
  const [lockOrg, setLockOrg] = useState(false);
  const [userTypeState, setUserTypeState] = useState('');
  const [isRootAdmin, setIsRootAdmin] = useState(false);
  const [orgLabel, setOrgLabel] = useState('');

  // Email dialog state
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailScope, setEmailScope] = useState('all'); // 'all' | 'selected'
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminOptions, setAdminOptions] = useState([]); // [{id, name, email}]
  const [selectedAdminIds, setSelectedAdminIds] = useState(new Set());

  const fmt = (d) => {
    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  // When opening email dialog, load admins for current org
  useEffect(() => {
    if (emailOpen) {
      fetchOrgAdmins();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailOpen, organizationId]);

  const toggleAdminSelection = (id) => {
    setSelectedAdminIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAllCurrentPage = (checked) => {
    if (checked) {
      setSelectedIds(new Set(rows.map((r) => r.documentId)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const daysUntil = (dateStr) => {
    if (!dateStr) return '';
    const today = new Date();
    const d = new Date(dateStr + 'T00:00:00Z');
    const diff = Math.ceil((d.getTime() - Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate())) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const effectiveTypes = useMemo(() => {
    const effective = types.includes('ALL') ? types.filter(t => t !== 'ALL') : types;
    return effective;
  }, [types]);

  // Expand canonical types to include legacy aliases seen in DB so filters match
  const expandTypesForQuery = (selected) => {
    const set = new Set();
    const push = (v) => { if (v && String(v).trim()) set.add(String(v).trim()); };
    const addCommonVariants = (base) => {
      if (!base) return;
      const up = base.toUpperCase();
      const low = base.toLowerCase();
      const snake = low.replace(/[-\s]/g, '_');
      const camel = snake.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
      [base, up, low, snake, camel].forEach(push);
    };
    (selected || []).forEach((t) => {
      switch (t) {
        case 'PASSPORT_COPY':
          addCommonVariants('PASSPORT_COPY');
          addCommonVariants('passport_copy');
          addCommonVariants('passportCopy');
          break;
        case 'EMIRATES_ID':
          addCommonVariants('EMIRATES_ID');
          addCommonVariants('emirates_id');
          addCommonVariants('emiratesId');
          break;
        case 'VISA_PAGE':
          addCommonVariants('VISA_PAGE');
          addCommonVariants('visa_page');
          addCommonVariants('visaPage');
          break;
        case 'PHOTO':
          addCommonVariants('PHOTO');
          addCommonVariants('photo');
          // include known variant used in older uploads
          push('photoMinified');
          break;
        case 'salaryCertificate':
          addCommonVariants('salaryCertificate');
          addCommonVariants('salary_certificate');
          break;
        default:
          addCommonVariants(t);
      }
    });
    return Array.from(set);
  };

  const handleEmailReport = async () => {
    setSendingEmail(true);
    try {
      const selectedEmails = adminOptions.filter(a => selectedAdminIds.has(a.id)).map(a => a.email).filter(Boolean);
      const additional = emailRecipients;
      const recipients = [
        ...selectedEmails,
        ...String(additional || '')
          .split(',')
          .map((e) => e.trim())
          .filter((e) => !!e)
      ].join(',');
      // Determine days value for email based on filter selection
      let daysForEmail;
      if (showAllDocs) {
        // Show all documents: send days as 150
        daysForEmail = 150;
      } else if (days === 'EXPIRED') {
        // Expired filter: send days as 0
        daysForEmail = 0;
      } else {
        // Regular days filter: use the selected value or default to 30
        const allowedDays = [30, 60, 90, 150];
        const numericDays = Number(days);
        daysForEmail = allowedDays.includes(numericDays) ? numericDays : 30;
      }
      const payload = {
        days: daysForEmail,
        recipients,
        summaryOnly: false,
        documentTypes: expandTypesForQuery(effectiveTypes),
        status: 'CURRENT',
      };
      // Add organizationId if available
      if (organizationId) {
        payload.organizationId = organizationId;
      }
      await sendDocumentReportEmail(payload);
      setEmailOpen(false);
      setEmailRecipients('');
      setSelectedAdminIds(new Set());
    } catch (e) {
      const status = e?.response?.status;
      const data = e?.response?.data;
      const msg = e?.message || 'Unknown error';
      console.error('Email report failed', { status, data, msg, error: e });
    } finally {
      setSendingEmail(false);
    }
  };

  const fetchOrgAdmins = async () => {
    if (!organizationId) {
      setAdminOptions([]);
      setSelectedAdminIds(new Set());
      return;
    }
    setAdminLoading(true);
    try {
      const qp = new URLSearchParams();
      qp.append('organisationId', organizationId);
      // This endpoint returns organisation details including primary email
      const url = `/api/organisations/details?${qp.toString()}`;
      const { data } = await apiClient.get(url);
      const email = data?.email || '';
      const name = data?.name || data?.contactPerson || 'Organization';
      const mapped = email
        ? [{ id: data?.id || organizationId, name, email }]
        : [];
      setAdminOptions(mapped);
      setSelectedAdminIds(new Set(mapped.map((m) => m.id))); // preselect org email if present
    } catch (e) {
      console.error('Failed to fetch organization admins', e);
      setAdminOptions([]);
      setSelectedAdminIds(new Set());
    } finally {
      setAdminLoading(false);
    }
  };

  const handleTileClick = (key) => {
    setTileKey((prev) => (prev === key ? '' : key));
    setPage(0);
    setSelectedIds(new Set());
  };

  const rangeForTile = (key) => {
    const today = new Date();
    const addDays = (n) => {
      const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
      d.setUTCDate(d.getUTCDate() + n);
      return d;
    };
    switch (key) {
      case 'TOTAL':
        return { fromDate: fmt(addDays(0)) };
      case 'R0_30':
        return { fromDate: fmt(addDays(0)), toDate: fmt(addDays(30)) };
      case 'R31_60':
        return { fromDate: fmt(addDays(31)), toDate: fmt(addDays(60)) };
      case 'R61_90':
        return { fromDate: fmt(addDays(61)), toDate: fmt(addDays(90)) };
      case 'EXPIRED':
        return { fromDate: undefined, toDate: fmt(addDays(-1)) };
      default:
        return {};
    }
  };

  const params = useMemo(() => {
    const p = { page, size };
    if (organizationId) p.organizationId = organizationId;
    if (!showAllDocs && days) {
      if (days === 'EXPIRED') {
        // For expired filter: include expired docs up to today
        p.includeExpired = true;
        p.toDate = new Date().toISOString().split('T')[0];
      } else {
        p.days = days;
      }
    }
    // Always request only current documents
    p.isCurrent = true;
    if (types && types.length) {
      // If ALL is selected, omit types param; else exclude ALL from list
      const effective = types.includes('ALL') ? types.filter(t => t !== 'ALL') : types;
      if (effective.length) {
        const expanded = expandTypesForQuery(effective);
        if (expanded.length) p.types = expanded.join(',');
      }
    }
    // Apply tile-based date range filtering to the LIST only
    if (tileKey) {
      const r = rangeForTile(tileKey);
      if (r.fromDate) p.fromDate = r.fromDate;
      if (r.toDate) p.toDate = r.toDate;
    }
    // Debug aid: ensure we are sending the expected filter
    try { console.debug('[DocReport] list params', p); } catch {}
    return p;
  }, [page, size, organizationId, days, isCurrent, showAllDocs, types, tileKey]);

  // Separate params for SUMMARY – do NOT include tileKey range so tiles remain overall counts
  const summaryParams = useMemo(() => {
    const p = {};
    if (organizationId) p.organizationId = organizationId;
    if (!showAllDocs && days) {
      if (days === 'EXPIRED') {
        // For expired filter: include expired docs up to today
        p.includeExpired = true;
        p.toDate = new Date().toISOString().split('T')[0];
      } else {
        p.days = days;
      }
    }
    p.isCurrent = true;
    if (types && types.length) {
      const effective = types.includes('ALL') ? types.filter(t => t !== 'ALL') : types;
      if (effective.length) {
        const expanded = expandTypesForQuery(effective);
        if (expanded.length) p.types = expanded.join(',');
      }
    }
    try { console.debug('[DocReport] summary params', p); } catch {}
    return p;
  }, [organizationId, days, isCurrent, showAllDocs, types]);

  const fetchTypes = async () => {
    try {
      // Primary: use processor registry (backend-registered document processors)
      const data = await fetchDocumentTypes();
      const unique = Array.from(new Set((data || []).filter(Boolean)));
      // Sort by user-friendly label, keep ALL at top
      const sorted = unique.sort((a, b) => labelForType(a).localeCompare(labelForType(b)));
      const opts = ['ALL', ...sorted];
      setTypeOptions(opts);
      // Reconcile current selection with available options
      setTypes((prev) => {
        if (!prev || prev.length === 0) return ['ALL'];
        if (prev.includes('ALL')) return ['ALL'];
        const intersection = prev.filter((t) => opts.includes(t));
        return intersection.length ? intersection : ['ALL'];
      });
    } catch (e) {
      // fallback: only ALL if service fails
      setTypeOptions(['ALL']);
      setTypes(['ALL']);
    }
  };

  const fetchOrgs = async () => {
    setOrgLoading(true);
    try {
      const list = await fetchCustomerOrganisations();
      setOrgOptions(list);
    } catch (e) {
      console.error('Failed to load organizations', e);
      setOrgOptions([]);
    } finally {
      setOrgLoading(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await listDocumentsReport(params);
      setRows(data?.content || []);
      setTotalElements(data?.totalElements || 0);
      // Reset selection when data changes
      setSelectedIds(new Set());
    } catch (e) {
      console.error('Failed to load document report', e);
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const { data } = await fetchDocumentsSummary(summaryParams);
      setSummary(data || null);
    } catch (e) {
      console.error('Failed to load report summary', e);
      setSummary(null);
    }
  };

  useEffect(() => {
    // Detect role and lock organization for customer org users or customer admins
    const init = async () => {
      try {
        const user = await getCurrentUser();
        const roles = (user?.roles || []).map((r) => String(r).toLowerCase());
        const userType = String(user?.userType || user?.principalInfo?.userType || '').toUpperCase();
        const isCustomerOrgUser = userType === 'CUSTOMER_ORG';
        const isCustomerAdmin = roles.some((r) => r.includes('customer') && r.includes('admin'));
        const isRootAdminUser = roles.includes('role_root_admin');
        const orgId = user?.organization?.id;
        const orgName = user?.organization?.name;
        const willLock = !!((isCustomerOrgUser || isCustomerAdmin) && orgId);
        if (willLock) {
          setOrganizationId(orgId);
          setLockOrg(true);
          setOrgLabel(orgName || 'My Organization');
        }
        setUserTypeState(userType);
        setIsRootAdmin(isRootAdminUser);
      } catch (e) {
        // ignore auth failures; default to admin behavior
      } finally {
        // Load types and orgs as applicable
        fetchTypes();
        // Use local decision instead of state which may not be updated synchronously
        const user = await getCurrentUser().catch(() => null);
        const roles = (user?.roles || []).map((r) => String(r).toLowerCase());
        const userType = String(user?.userType || user?.principalInfo?.userType || '').toUpperCase();
        const isCustomerOrgUser = userType === 'CUSTOMER_ORG';
        const isCustomerAdmin = roles.some((r) => r.includes('customer') && r.includes('admin'));
        const willLock = !!((isCustomerOrgUser || isCustomerAdmin) && user?.organization?.id);
        if (!willLock) {
          fetchOrgs();
        }
        setUserTypeState(userType);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload document types when organization changes
  useEffect(() => {
    fetchTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryParams]);

  // If tile filter changes, ensure we are on the first page
  useEffect(() => {
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tileKey]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const { blob } = await exportDocumentsReportCsv(params);
      downloadBlob(blob, `document-report-${new Date().toISOString().slice(0,10)}.csv`);
    } catch (e) {
      console.error('Export failed', e);
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadRow = async (row) => {
    try {
      if (!row?.filePath) return;
      const url = API_ENDPOINTS.FILES.DOWNLOAD(row.filePath);
      const resp = await apiClient.get(url, { responseType: 'blob' });
      downloadBlob(resp.data, row.fileName || 'document');
    } catch (e) {
      console.error('Row download failed', e);
    }
  };

  const handleViewRow = async (row) => {
    try {
      if (!row?.filePath) return;
      const url = API_ENDPOINTS.FILES.DOWNLOAD(row.filePath);
      const resp = await apiClient.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(resp.data);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.error('Row view failed', e);
    }
  };

  const handleTypesChange = (event) => {
    const value = event.target.value;
    // Special handling for ALL: if selected, it becomes the only selection; if other selected, remove ALL
    if (value.includes('ALL')) {
      setTypes(['ALL']);
    } else {
      setTypes(value);
    }
  };

  // Compute whether to hide Organization selector for Customer Org users/admins as early as possible
  let shouldHideOrgSelector = false;
  try {
    const uiUserType = String(userTypeState || '').toUpperCase();
    const authInfoRaw = localStorage.getItem('authInfo');
    const authInfo = authInfoRaw ? JSON.parse(authInfoRaw) : {};
    const rolesSrc = authInfo?.roles || authInfo?.user?.roles || [];
    const rolesLower = (rolesSrc || []).map((r) => String(r).toLowerCase());
    const userTypeImmediate = String(
      authInfo?.userType || authInfo?.user?.userType || authInfo?.principalInfo?.userType || ''
    ).toUpperCase();
    const hasOrgImmediate = !!(
      authInfo?.organization?.id || authInfo?.user?.organization?.id || localStorage.getItem('organizationCode')
    );
    const isCustomerImmediate =
      rolesLower.some((r) => r.includes('customer')) || userTypeImmediate.includes('CUSTOMER');
    const isCustomerOrgImmediate = userTypeImmediate.includes('CUSTOMER') && userTypeImmediate.includes('ORG');
    const isCustomerAdminImmediate = rolesLower.some(
      (r) => r.includes('customer_admin') || (r.includes('customer') && r.includes('admin'))
    );

    shouldHideOrgSelector =
      lockOrg ||
      uiUserType === 'CUSTOMER_ORG' ||
      isCustomerOrgImmediate ||
      (isCustomerAdminImmediate && hasOrgImmediate) ||
      (isCustomerImmediate && hasOrgImmediate);
  } catch (e) {
    // ignore parse errors; default will be false
  }

  return (
    <Stack spacing={2} sx={{ mb: 1 }}>
      <Typography
        variant="h6"
        sx={{
          fontSize: 18,
          fontWeight: 700,
          color: '#0f172a',
          display: 'flex',
          alignItems: 'center',
          '&::before': {
            content: '""',
            display: 'inline-block',
            width: 6,
            height: 18,
            borderRadius: 2,
            backgroundColor: '#0ea5e9',
            marginRight: 10,
          },
        }}
      >
        Expiring Documents
      </Typography>

      <Card elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-end" sx={{ '& label, & .MuiFormLabel-root': { fontSize: 13 } }}>
            {!shouldHideOrgSelector ? (
              <Autocomplete
                size="small"
                sx={{ minWidth: 300 }}
                options={orgOptions}
                loading={orgLoading}
                getOptionLabel={(opt) => opt?.name || ''}
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                value={orgOptions.find((o) => o.id === organizationId) || null}
                onChange={(_e, val) => setOrganizationId(val?.id || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Organization"
                    placeholder="Select organization"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {orgLoading ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            ) : null}

            {!showAllDocs && (
              <FormControl sx={{ minWidth: 160 }} size="small">
                <InputLabel id="days-label">Days</InputLabel>
                <Select
                  labelId="days-label"
                  value={days}
                  label="Days"
                  onChange={(e) => setDays(e.target.value)}
                >
                  {daysOptions.map((d) => (
                    <MenuItem key={d} value={d}>
                      {d === 'EXPIRED' ? 'Expired' : d}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {null /* Status control removed: always showing only current */}

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <input
                  type="checkbox"
                  id="show-all-docs"
                  checked={showAllDocs}
                  onChange={(e) => setShowAllDocs(e.target.checked)}
                  style={{ width: 16, height: 16 }}
                />
                <label htmlFor="show-all-docs" style={{ fontSize: 13 }}>Show all documents</label>
              </Stack>
            </FormControl>

            <FormControl sx={{ minWidth: 260 }} size="small">
              <InputLabel id="types-label">Document Types</InputLabel>
              <Select
                multiple
                labelId="types-label"
                value={types}
                onChange={handleTypesChange}
                input={<OutlinedInput label="Document Types" />}
                renderValue={(selected) => selected.map(labelForType).join(', ')}
              >
                {typeOptions.map((t) => (
                  <MenuItem key={t} value={t}>{labelForType(t)}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              color="primary"
              startIcon={<SearchIcon />}
              onClick={load}
              disabled={loading}
              size="small"
            >
              {loading ? 'Loading...' : 'Refresh'}
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              startIcon={exporting ? <CircularProgress size={18} /> : <DownloadIcon />}
              onClick={handleExport}
              disabled={exporting}
              size="small"
              sx={{
                whiteSpace: 'nowrap',
                textTransform: 'none',
                fontSize: 12,
                fontWeight: 600,
                // Ensure label visible on light backgrounds
                color: (theme) => theme.palette.mode === 'dark' ? theme.palette.secondary.light : '#1f2937',
                borderColor: (theme) => theme.palette.mode === 'dark' ? theme.palette.secondary.light : '#94a3b8',
                '&:hover': {
                  borderColor: (theme) => theme.palette.mode === 'dark' ? theme.palette.secondary.main : '#64748b',
                },
                // Improve disabled contrast
                '&.Mui-disabled': {
                  color: '#64748b',
                  borderColor: '#cbd5e1',
                  opacity: 1,
                },
                // Ensure icon adopts label color
                '& .MuiButton-startIcon': {
                  color: 'inherit',
                },
              }}
            >
              Export CSV
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              startIcon={<EmailIcon />}
              onClick={() => setEmailOpen(true)}
              size="small"
              sx={{
                whiteSpace: 'nowrap',
                textTransform: 'none',
                fontSize: 12,
                fontWeight: 600,
                color: (theme) => theme.palette.mode === 'dark' ? theme.palette.secondary.light : '#1f2937',
                borderColor: (theme) => theme.palette.mode === 'dark' ? theme.palette.secondary.light : '#94a3b8',
                '&:hover': {
                  borderColor: (theme) => theme.palette.mode === 'dark' ? theme.palette.secondary.main : '#64748b',
                },
              }}
            >
              Email Report
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {summary && (
        <Card elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 2 }}>
          <CardContent>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <SummaryTile
                title="Total Expiring"
                value={summary.totalExpiring}
                color="#1976d2"
                onClick={() => handleTileClick('TOTAL')}
                selected={tileKey === 'TOTAL'}
              />
              <SummaryTile
                title="0 - 30 days"
                value={summary.expiringIn_0_30}
                color="#2e7d32"
                onClick={() => handleTileClick('R0_30')}
                selected={tileKey === 'R0_30'}
              />
              <SummaryTile
                title="31 - 60 days"
                value={summary.expiringIn_31_60}
                color="#ed6c02"
                onClick={() => handleTileClick('R31_60')}
                selected={tileKey === 'R31_60'}
                valueSx={{ fontSize: 24, lineHeight: 1.2 }}
              />
              <SummaryTile
                title="61 - 90 days"
                value={summary.expiringIn_61_90}
                color="#9c27b0"
                onClick={() => handleTileClick('R61_90')}
                selected={tileKey === 'R61_90'}
              />
              <SummaryTile
                title="Already Expired"
                value={summary.alreadyExpired}
                color="#d32f2f"
                onClick={() => handleTileClick('EXPIRED')}
                selected={tileKey === 'EXPIRED'}
              />
            </Stack>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent>
          <Box sx={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ width: 36, textAlign: 'left', borderBottom: '1px solid #eee', padding: '8px' }}>
                    <input
                      type="checkbox"
                      checked={rows.length > 0 && selectedIds.size === rows.length}
                      onChange={(e) => toggleSelectAllCurrentPage(e.target.checked)}
                      aria-label="select all"
                    />
                  </th>
                  {['Employee Name', 'Employee ID', 'Organization', 'Doc Type', 'File Name', 'Expire In days', 'Expiry Date', 'Actions'].map((h) => (
                    <th key={h} style={{ textAlign: 'left', borderBottom: '1px solid #eee', padding: '8px', fontSize: 13, fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.documentId}>
                    <td style={{ padding: '8px', borderBottom: '1px solid #f5f5f5' }}>
                      <input type="checkbox" checked={selectedIds.has(r.documentId)} onChange={() => toggleSelect(r.documentId)} aria-label="select row" />
                    </td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #f5f5f5', fontSize: 13 }}>{[r.firstName, r.lastName].filter(Boolean).join(' ')}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #f5f5f5', fontSize: 13 }}>{r.employeeId}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #f5f5f5', fontSize: 13 }}>{r.organizationName}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #f5f5f5', fontSize: 13 }}>{r.documentType}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #f5f5f5', fontSize: 13 }}>{r.fileName}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #f5f5f5', fontSize: 13 }}>{daysUntil(r.expiryDate)}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #f5f5f5', fontSize: 13 }}>{r.expiryDate}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #f5f5f5' }}>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="View">
                          <IconButton size="small" onClick={() => handleViewRow(r)} aria-label="view">
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download">
                          <IconButton size="small" onClick={() => handleDownloadRow(r)} aria-label="download">
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </td>
                  </tr>
                ))}
                {!rows.length && (
                  <tr>
                    <td colSpan={9} style={{ padding: '16px', textAlign: 'center', color: '#888', fontSize: 13 }}>
                      {loading ? 'Loading...' : 'No data'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Box>
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" mt={2}>
            <Typography variant="body2">Total: {totalElements}</Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || loading}
                sx={{
                  color: '#1f2937',
                  borderColor: '#94a3b8',
                  '&:hover': { borderColor: '#64748b' },
                  '&.Mui-disabled': { color: '#64748b', borderColor: '#cbd5e1', opacity: 1 },
                }}
              >
                Prev
              </Button>
              <TextField
                label="Page"
                size="small"
                type="number"
                value={page + 1}
                onChange={(e) => setPage(Math.max(0, Number(e.target.value) - 1))}
                sx={{ width: 100 }}
              />
              <TextField
                label="Size"
                size="small"
                type="number"
                value={size}
                onChange={(e) => setSize(Math.max(1, Number(e.target.value)))}
                sx={{ width: 100 }}
              />
              <Button
                variant="outlined"
                size="small"
                disableElevation
                onClick={() => setPage((p) => p + 1)}
                disabled={loading}
                sx={{
                  // Match PREV exactly
                  backgroundColor: 'transparent !important',
                  color: '#1f2937 !important',
                  borderColor: '#94a3b8 !important',
                  '&:hover': { borderColor: '#64748b !important', backgroundColor: 'transparent !important' },
                  '&.Mui-disabled': { color: '#64748b !important', borderColor: '#cbd5e1 !important', opacity: 1, backgroundColor: 'transparent !important' },
                }}
              >
                Next
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Email Report Dialog */}
      <Dialog open={emailOpen} onClose={() => setEmailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Document Report</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Typography variant="body2">
              Report will be generated from current filters{selectedIds.size ? `; ${selectedIds.size} selected on page` : ''}.
            </Typography>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Organization Admins</Typography>
              {adminLoading ? (
                <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={16} /> <Typography variant="caption">Loading admins...</Typography></Stack>
              ) : (
                <Stack spacing={0.5}>
                  {adminOptions.length === 0 ? (
                    <Typography variant="caption" sx={{ color: '#666' }}>No admins found for this organization.</Typography>
                  ) : (
                    <>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={adminOptions.length > 0 && selectedAdminIds.size === adminOptions.length}
                            indeterminate={selectedAdminIds.size > 0 && selectedAdminIds.size < adminOptions.length}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setSelectedAdminIds(checked ? new Set(adminOptions.map(a => a.id)) : new Set());
                            }}
                          />
                        }
                        label={<Typography variant="caption">Select all</Typography>}
                      />
                      {adminOptions.map((a) => (
                        <FormControlLabel
                          key={a.id}
                          control={<Checkbox size="small" checked={selectedAdminIds.has(a.id)} onChange={() => toggleAdminSelection(a.id)} />}
                          label={<Typography variant="body2">{a.name} <Typography component="span" variant="caption" sx={{ color: '#555' }}>({a.email})</Typography></Typography>}
                        />
                      ))}
                    </>
                  )}
                </Stack>
              )}
            </Box>
            <TextField
              label="Additional Recipients (comma-separated emails)"
              fullWidth
              value={emailRecipients}
              onChange={(e) => setEmailRecipients(e.target.value)}
              placeholder="admin@example.com, ops@example.com"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailOpen(false)} disabled={sendingEmail}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleEmailReport}
            disabled={
              sendingEmail || (selectedAdminIds.size === 0 && !emailRecipients.trim())
            }
            startIcon={sendingEmail ? <CircularProgress size={16} /> : <EmailIcon />}
          >
            {sendingEmail ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
